import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { AuthModalContext } from "../../App";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const { user, logout, isLoggedIn, isAdmin, isBlogAdmin } = useUser();
  const dropRef = useRef(null);
  const authCtx = useContext(AuthModalContext);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const links = [
    { path: "/", label: "Home", id: "home" },
    { path: "/explore", label: "Explore", id: "explore" },
    { path: "/compare", label: "⚖ Compare", id: "compare" },
    {
      path: "/finder",
      label: "🎯 Find My Course",
      id: "finder",
      style: { color: "#ff6b35", fontWeight: 600 },
    },
    {
      path: "/jobs",
      label: "💼 Jobs",
      id: "jobs",
      style: { color: "#10b981", fontWeight: 600 },
    },
    { path: "/tools", label: "🎛 Tools", id: "tools" },
    { path: "/blog", label: "📝 Blog", id: "blog" },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    setDropOpen(false);
    setMenuOpen(false);
    logout();
    navigate("/");
  };

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "U";

  return (
    <nav>
      <div
        className="nav-brand"
        onClick={() => navigate("/")}
        style={{ cursor: "pointer", alignItems: "flex-start" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <video
            src="/skillyer.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="logo-video"
            style={{ height: 40 }}
          />
        </div>
      </div>

      <div className="nav-center">
        {location.pathname !== "/" && (
          <button className="back-btn visible" onClick={() => navigate(-1)}>
            ← Back
          </button>
        )}
      </div>

      <ul className={`nav-links${menuOpen ? " open" : ""}`} id="navLinks">
        {links.map((link) => (
          <li key={link.id}>
            <a
              href={link.path}
              onClick={(e) => {
                e.preventDefault();
                navigate(link.path);
                setMenuOpen(false);
              }}
              className={isActive(link.path) ? "active" : ""}
              style={!isActive(link.path) ? link.style || {} : {}}
            >
              {link.label}
            </a>
          </li>
        ))}

        {/* Guest: show Login & Register buttons */}
        {!isLoggedIn && (
          <>
            <li style={{ listStyle: "none" }}>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  authCtx?.openAuthModal("login");
                }}
                style={{
                  background: "rgba(255,255,255,0.12)",
                  border: "1.5px solid rgba(255,255,255,0.3)",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "7px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 0.15s",
                  marginRight: "10px",
                }}
              >
                Login
              </button>
            </li>
            <li style={{ listStyle: "none" }}>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  authCtx?.openAuthModal("register");
                }}
                style={{
                  background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                  border: "none",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "7px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Register
              </button>
            </li>
          </>
        )}

        {/* Logged-in user avatar + dropdown */}
        {isLoggedIn && (
          <li ref={dropRef} style={{ position: "relative", listStyle: "none" }}>
            <button
              onClick={() => setDropOpen((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: isAdmin
                    ? "linear-gradient(135deg, #dc2626, #7f1d1d)"
                    : "linear-gradient(135deg, #667eea, #764ba2)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  maxWidth: 100,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user?.name?.split(" ")[0]}
              </span>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>
                ▾
              </span>
            </button>

            {dropOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 10px)",
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 8px 30px rgba(0,0,0,0.14)",
                  minWidth: 200,
                  zIndex: 9999,
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <div
                    style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}
                  >
                    {user?.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                    {user?.email}
                  </div>
                  {isAdmin && (
                    <span
                      style={{
                        display: "inline-block",
                        marginTop: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#dc2626",
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 6,
                        padding: "1px 7px",
                      }}
                    >
                      🔴 Admin
                    </span>
                  )}
                  {!isAdmin && isBlogAdmin && (
                    <span
                      style={{
                        display: "inline-block",
                        marginTop: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#1d4ed8",
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        borderRadius: 6,
                        padding: "1px 7px",
                      }}
                    >
                      ✍️ Blog Admin
                    </span>
                  )}
                </div>

                {isAdmin && (
                  <button
                    onClick={() => {
                      setDropOpen(false);
                      setMenuOpen(false);
                      navigate("/admin");
                    }}
                    style={{
                      width: "100%",
                      padding: "11px 16px",
                      background: "#fef2f2",
                      border: "none",
                      borderBottom: "1px solid #f3f4f6",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: 13,
                      color: "#dc2626",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    🛡️ Admin Dashboard
                  </button>
                )}

                {!isAdmin && isBlogAdmin && (
                  <button
                    onClick={() => {
                      setDropOpen(false);
                      setMenuOpen(false);
                      navigate("/admin/blogs");
                    }}
                    style={{
                      width: "100%",
                      padding: "11px 16px",
                      background: "#eff6ff",
                      border: "none",
                      borderBottom: "1px solid #f3f4f6",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: 13,
                      color: "#1d4ed8",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    ✍️ Blog Admin
                  </button>
                )}

                <button
                  onClick={handleLogout}
                  style={{
                    width: "100%",
                    padding: "11px 16px",
                    background: "none",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: 13,
                    color: "#dc2626",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  🚪 Sign Out
                </button>
              </div>
            )}
          </li>
        )}
      </ul>

      <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
        <span />
        <span />
        <span />
      </button>
    </nav>
  );
};

export default Navbar;
